import { useLocation, useParams, useNavigate } from "react-router-dom"
import { useContainerInjection } from "../../hooks/useContainerInjection"
import { createRef, useEffect, useState } from "react"
import { observer } from "mobx-react"
import MainForm, { MainFormRef } from "../../components/Form/MainForm"
import { Col, Row } from "antd"
import { useTranslation } from "react-i18next"
import { Card } from "../../styledComponents/CustomCard/Card"
import { SurveysViewModel } from "./SurveysViewModel"
import { SurveySection } from "../../../domain/entities/SurveySection"
import SectionInformationForm from "./components/SectionInformationForm"
import SectionQuestionsForm from "./components/SectionQuestionsForm"
import { RoutePaths } from "../../navigation/RoutePaths.enum"

const SurveySectionFormView = () => {
	const { t } = useTranslation("surveys", { keyPrefix: "form" })
	const locationState: { surveySection: SurveySection } = useLocation().state
	const { id } = useParams<{ id: string }>()
	const navigate = useNavigate()
	const formRef = createRef<MainFormRef>()
	const viewModel = useContainerInjection<SurveysViewModel>("SurveysViewModel")
	const [formModified, setFormModified] = useState(false)
	useEffect(() => {
		const initializeForm = async () => {
			let surveySection: SurveySection | undefined = locationState?.surveySection

			if (id && !surveySection) {
				await viewModel.fetchSurveySections()
				surveySection = viewModel.surveySections.find(s => s.id === parseInt(id))
			}

			viewModel.setEditMode(surveySection?.id !== undefined)
			const formData = {
				id: surveySection?.id,
				name: surveySection?.name || "",
				name_en: surveySection?.name_en || "",
				name_ca: surveySection?.name_ca || "",
				description: surveySection?.description || "",
				description_en: surveySection?.description_en || "",
				description_ca: surveySection?.description_ca || "",
				image: surveySection?.image,
				order: surveySection?.order,
				questions: surveySection?.questions || []
			}
			viewModel.setSectionFormData(formData)
			viewModel.setError("")
		}

		initializeForm()
	}, [locationState?.surveySection, id])

	const onSubmitForm = async () => {
		if (viewModel.isEditMode) {
			await viewModel.updateSurveySection()
		} else {
			await viewModel.createSurveySection()
			if (viewModel.sectionFormData.id) {
				navigate(RoutePaths.SURVEY_SECTIONS_EDIT.replace(":id", viewModel.sectionFormData.id.toString()))
			}
		}
		setFormModified(false)
	}

	return (
		<div>
			<MainForm
				onSubmitForm={onSubmitForm}
				initialValues={viewModel.sectionFormData}
				formModified={formModified}
				ref={formRef}
			>
				<Row gutter={[24, 0]}>
					<Col xl={24} md={24} xs={24}>
						<Row gutter={[24, 0]}>
							<Col md={24} xl={24} xs={24}>
								<Card
									bordered={false}
									className="criclebox tablespace mb-24"
									title={<h6 className="font-semibold m-0">{t("sectionInfo.cardTitle")}</h6>}
									bodyStyle={{ padding: "24px", paddingLeft: "2.5rem" }}
								>
									<SectionInformationForm
										formData={viewModel.sectionFormData}
										onChange={formData => {
											viewModel.setSectionFormData(formData)
											setFormModified(true)
										}}
									/>
								</Card>
								<Card
									bordered={false}
									className="criclebox tablespace mb-24"
									title={<h6 className="font-semibold m-0">{t("sections.cardTitle")}</h6>}
									bodyStyle={{ padding: "24px", paddingLeft: "2.5rem" }}
								>
									<SectionQuestionsForm
										formData={viewModel.sectionFormData}
										onChange={formData => {
											viewModel.setSectionFormData(formData)
											setFormModified(true)
										}}
									/>
								</Card>
							</Col>
						</Row>
					</Col>
				</Row>
			</MainForm>
		</div>
	)
}

export default observer(SurveySectionFormView)
